import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { Button, Text } from '@/components/ui';
import {
  createTransaction,
  fetchAccounts,
  fetchCategories,
  fetchCurrencies,
  fetchFxRates,
  fetchTransaction,
  updateTransaction,
  type TransactionInput,
} from '@/lib/finance/api';
import { categoryOptions, formatUsd, roundUsd } from '@/lib/finance/format';
import { monthStart } from '@/lib/finance/month';
import type { Account, Category, Currency } from '@/lib/finance/types';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

type FormState = {
  date: string;
  categoryId: string;
  accountId: string;
  toAccountId: string;
  currencyCode: string;
  amount: string;
  notes: string;
};

const EMPTY: FormState = {
  date: todayIso(),
  categoryId: '',
  accountId: '',
  toAccountId: '',
  currencyCode: 'USD',
  amount: '',
  notes: '',
};

export function RecordTransaction() {
  const router = useRouter();
  const params = useLocalSearchParams<{ id?: string }>();
  const editingId = params.id && /^\d+$/.test(params.id) ? Number(params.id) : null;

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [currencyTouched, setCurrencyTouched] = useState(false);

  const [form, setForm] = useState<FormState>(EMPTY);

  const [rate, setRate] = useState<number | null>(null);
  const [rateMissing, setRateMissing] = useState(false);

  const options = useMemo(() => categoryOptions(categories), [categories]);
  const selectedCategory = options.find((o) => String(o.id) === form.categoryId) ?? null;
  const isTransfer = selectedCategory?.kind === 'transfer';

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [accountRows, categoryRows, currencyRows] = await Promise.all([
          fetchAccounts(),
          fetchCategories(),
          fetchCurrencies(),
        ]);
        if (cancelled) return;
        setAccounts(accountRows);
        setCategories(categoryRows);
        setCurrencies(currencyRows);

        if (editingId != null) {
          const tx = await fetchTransaction(editingId);
          if (cancelled) return;
          setForm({
            date: tx.date,
            categoryId: tx.categoryId != null ? String(tx.categoryId) : '',
            accountId: tx.accountId != null ? String(tx.accountId) : '',
            toAccountId: tx.toAccountId != null ? String(tx.toAccountId) : '',
            currencyCode: tx.currencyCode,
            amount: String(tx.amount),
            notes: tx.notes ?? '',
          });
          setCurrencyTouched(true);
        } else {
          setForm((f) => ({ ...f, currencyCode: currencyRows[0]?.code ?? 'USD' }));
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load form data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [editingId]);

  // Resolve the FX rate for the chosen month + currency to drive the USD preview.
  useEffect(() => {
    let cancelled = false;
    if (form.currencyCode === 'USD') {
      setRate(1);
      setRateMissing(false);
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) {
      setRate(null);
      setRateMissing(false);
      return;
    }
    const month = monthStart(form.date.slice(0, 7));
    void fetchFxRates(month, month)
      .then((rates) => {
        if (cancelled) return;
        const found = rates.find((r) => r.currencyCode === form.currencyCode);
        setRate(found?.rateToUsd ?? null);
        setRateMissing(found?.rateToUsd == null);
      })
      .catch(() => {
        if (!cancelled) {
          setRate(null);
          setRateMissing(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [form.date, form.currencyCode]);

  const amountNumber = Number(form.amount);
  const amountValid =
    form.amount.trim() !== '' && Number.isFinite(amountNumber) && amountNumber !== 0;
  const usdPreview = amountValid && rate != null && rate > 0 ? roundUsd(amountNumber / rate) : null;

  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  function onAccountChange(value: string) {
    setForm((f) => {
      const next = { ...f, accountId: value };
      if (!currencyTouched) {
        const account = accounts.find((a) => String(a.id) === value);
        if (account) next.currencyCode = account.currencyCode;
      }
      return next;
    });
  }

  function buildPayload(): TransactionInput | null {
    if (!amountValid) {
      setError('Enter an amount other than zero');
      return null;
    }
    if (isTransfer) {
      if (!form.accountId || !form.toAccountId) {
        setError('A transfer needs both a from-account and a to-account');
        return null;
      }
      if (form.accountId === form.toAccountId) {
        setError('Transfer accounts must differ');
        return null;
      }
    }
    return {
      date: form.date,
      categoryId: form.categoryId ? Number(form.categoryId) : null,
      accountId: form.accountId ? Number(form.accountId) : null,
      toAccountId: isTransfer && form.toAccountId ? Number(form.toAccountId) : null,
      currencyCode: form.currencyCode,
      amount: amountNumber,
      notes: form.notes.trim() || null,
    };
  }

  async function onSave(addAnother: boolean) {
    const payload = buildPayload();
    if (!payload) return;
    setSaving(true);
    setError(null);
    try {
      if (editingId != null) {
        await updateTransaction(editingId, payload);
      } else {
        await createTransaction(payload);
      }
      if (addAnother && editingId == null) {
        // Keep date + account for fast batch entry; clear amount/category/notes.
        setForm((f) => ({ ...f, categoryId: '', amount: '', notes: '', toAccountId: '' }));
      } else {
        router.push('/finance/transactions');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save transaction');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <ActivityIndicator className="mt-8" />;
  }

  return (
    <View className="max-w-xl">
      <Text variant="h2">{editingId != null ? 'Edit transaction' : 'Record transaction'}</Text>

      <div className="mt-5 flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="cs-text-label">Date</span>
          <input
            className="cs-input"
            type="date"
            value={form.date}
            onChange={(e) => setField('date', e.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="cs-text-label">Category</span>
          <select
            className="cs-input"
            value={form.categoryId}
            onChange={(e) => setField('categoryId', e.target.value)}
          >
            <option value="">Uncategorized</option>
            {options.map((option) => (
              <option key={option.id} value={String(option.id)}>
                {option.label}
                {option.kind !== 'expense' ? ` (${option.kind})` : ''}
                {!option.isActive ? ' — inactive' : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="cs-text-label">
            {isTransfer ? (amountNumber < 0 ? 'To account' : 'From account') : 'Account'}
          </span>
          <select
            className="cs-input"
            value={form.accountId}
            onChange={(e) => onAccountChange(e.target.value)}
          >
            <option value="">None</option>
            {accounts.map((account) => (
              <option key={account.id} value={String(account.id)}>
                {account.name} ({account.currencyCode})
              </option>
            ))}
          </select>
        </label>

        {isTransfer ? (
          <label className="flex flex-col gap-1">
            <span className="cs-text-label">
              {amountNumber < 0 ? 'From account' : 'To account'}
            </span>
            <select
              className="cs-input"
              value={form.toAccountId}
              onChange={(e) => setField('toAccountId', e.target.value)}
            >
              <option value="">Select…</option>
              {accounts.map((account) => (
                <option key={account.id} value={String(account.id)}>
                  {account.name} ({account.currencyCode})
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <div className="flex flex-row gap-3">
          <label className="flex w-32 flex-col gap-1">
            <span className="cs-text-label">Currency</span>
            <select
              className="cs-input"
              value={form.currencyCode}
              onChange={(e) => {
                setCurrencyTouched(true);
                setField('currencyCode', e.target.value);
              }}
            >
              {currencies.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.code}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-1 flex-col gap-1">
            <span className="cs-text-label">Amount</span>
            <input
              className="cs-input"
              inputMode="decimal"
              placeholder="0.00"
              value={form.amount}
              onChange={(e) => setField('amount', e.target.value)}
            />
            <span className="cs-text-caption text-muted">
              A negative amount is a credit (money in). A positive amount is a debit (money out). A
              positive amount on an income category stays income.
            </span>
          </label>
        </div>

        <View className="rounded-lg bg-surface-raised px-3 py-2">
          {form.currencyCode === 'USD' ? (
            <Text variant="caption" className="text-muted">
              USD amount: {usdPreview != null ? `$${formatUsd(usdPreview)}` : '—'}
            </Text>
          ) : rateMissing ? (
            <View className="flex-row flex-wrap items-center gap-1">
              <Text variant="caption" className="text-fg-muted">
                No FX rate for {form.currencyCode} in {form.date.slice(0, 7)}.
              </Text>
              <Pressable onPress={() => router.push('/finance/master/fx')}>
                <Text variant="caption" className="font-semibold text-accent">
                  Add FX rate
                </Text>
              </Pressable>
            </View>
          ) : (
            <Text variant="caption" className="text-muted">
              USD amount: {usdPreview != null ? `$${formatUsd(usdPreview)}` : '—'}
              {rate != null ? `  ·  rate ${rate} / USD` : ''}
            </Text>
          )}
        </View>

        <label className="flex flex-col gap-1">
          <span className="cs-text-label">Notes</span>
          <input
            className="cs-input"
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
          />
        </label>

        {error ? <span className="cs-text-caption text-danger">{error}</span> : null}

        <View className="mt-2 flex-row flex-wrap gap-2">
          <Button
            label={saving ? 'Saving…' : 'Save'}
            variant="primary"
            size="sm"
            disabled={saving}
            onPress={() => void onSave(false)}
          />
          {editingId == null ? (
            <Button
              label="Save & add another"
              variant="secondary"
              size="sm"
              disabled={saving}
              onPress={() => void onSave(true)}
            />
          ) : null}
          <Button
            label="Cancel"
            variant="ghost"
            size="sm"
            onPress={() => router.push('/finance/transactions')}
          />
        </View>
      </div>
    </View>
  );
}
